import React, { useContext } from "react";
import { string, instanceOf, bool } from "prop-types";
import * as i18n from "util/i18n";
import FormattedDate from "pages/_components/FormattedDate";
import { transactionStatus } from "pages/transactions/constants";
import { TransactionContext } from "./TransactionContext";
import CustomData from "../CustomData/CustomData";

export const TransactionSign = ({
    creationLastName,
    creationFirstName,
    signatures: _signatures,
    showSignatures,
    showSignaturesInPending,
    status,
    noDate,
    creationTime,
    isSmall,
    hasSignatures,
    customText,
    hasVaucher,
    hasVoucherInPending,
    noHorizontalDivider,
    showMoreData,
    isDesktop,
}) => {
    const { transaction } = useContext(TransactionContext);
    const {
        userCreatorLastName = creationLastName,
        userCreatorFirstName = creationFirstName,
        idUserCreator,
        signatures = _signatures,
        idTransactionStatus = status,
        modificationDateTime,
        userEditorLastName,
        userEditorFirstName,
        creationDateTime,
        idTransaction,
        submitDateTime,
    } = transaction ?? {};

    const userCreatorSignLevel = signatures?.find((v) => v.idUser === idUserCreator)?.signatureLevel;
    const userCreatorSignLevelString = userCreatorSignLevel ? ` (${userCreatorSignLevel})` : "";

    const isFinished = idTransactionStatus === transactionStatus.FINISHED;
    const isPending =
        idTransactionStatus === transactionStatus.PENDING || idTransactionStatus === transactionStatus.PROCESSING;
    const isCancelled = idTransactionStatus === transactionStatus.CANCELLED;

    const isFailed = idTransactionStatus === transactionStatus.FAILED;

    const renderSignatures = (showSignaturesInPending && isPending) || (showSignatures && !isCancelled && isFinished) || (showSignatures && isFailed);
    const renderVoucher = (hasVoucherInPending && isPending) || (hasVaucher && isFinished) || isFailed;
    
    const SignituresData = () => (
        <>
            <div className="pending-approval">
                <div className="tiny-small secondary">{i18n.get(customText || "pending.approval")}</div>
                <div className="tiny-small font-bold secondary">
                    <FormattedDate
                        className="small"
                        date={signatures[0]?.creationDateTime}
                        dateFormat="DD/MM/YYYY HH:mm:ss"
                    />
                </div>
            </div>
        </>
    );

    return (
        <div className={isDesktop ? "signatures-data" : `signatures-data ${showMoreData ? "expand" : "collapse"}`}>
            {!noHorizontalDivider && (
                <div className="ticket__divider signatures-divider inside-ticket__body element-to-ignore">
                    <div className="ticket__notch ticket__notch--left element-to-ignore"> </div>
                    <div className="ticket__notch ticket__notch--right element-to-ignore"> </div>
                </div>
            )}
            <h2>{i18n.get("forms.transaction.ticket.title")}</h2>
            <div className="createdby-signature">
                <CustomData
                    titleText="forms.transaction.ticket.createdBy"
                    titleTextType="I18N"
                    valueText={`${userCreatorLastName}, ${userCreatorFirstName}${userCreatorSignLevelString}`}
                />
                {creationTime && (
                    <div className="tiny-small font-bold secondary">
                        <FormattedDate className="small" date={creationDateTime} dateFormat="DD/MM/YYYY HH:mm:ss" />
                    </div>
                )}
            </div>
            {renderSignatures && (
                <div className="approval-signature custom-data">
                    <div className="custom-text custom-data-title ">
                        {i18n.get("forms.transaction.ticket.signedBy")}
                    </div>
                    {signatures?.map((signature, index) => (
                        <span className="custom-text custom-data-value data-label">
                            {`${signature.userFullName} (${
                                signature.signatureLevel ? signature.signatureLevel : "No Firma"
                            }) ${!isSmall ? "-" : ""} `}
                            {!isSmall && (
                                <FormattedDate date={signature.creationDateTime} dateFormat="DD/MM/YYYY HH:mm:ss" />
                            )}
                            {index !== signatures.length - 1 && ", "}
                            {isSmall && (
                                <div className="tiny-small font-bold secondary">
                                    <FormattedDate date={signature.creationDateTime} dateFormat="DD/MM/YYYY HH:mm:ss" />
                                </div>
                            )}
                        </span>
                    ))}
                </div>
            )}
            {isCancelled && (
                <div className="canceled-signature">
                    <CustomData
                        titleText="forms.transaction.ticket.cancelledBy"
                        titleTextType="I18N"
                        valueText={`${userEditorLastName}, ${userEditorFirstName} `}
                    />
                    <div className="tiny-small font-bold secondary">
                        <FormattedDate date={submitDateTime} dateFormat="DD/MM/YYYY HH:mm:ss" />
                    </div>
                </div>
            )}
            {isPending && !noDate && (
                <div className="pending-signature">
                    {!noHorizontalDivider && <div className="horizontal-divider" />}
                    <SignituresData />
                </div>
            )}
            {isFinished && hasSignatures && (
                <div className="pending-signature">
                    {!noHorizontalDivider && <div className="horizontal-divider" />}
                    <SignituresData />
                    <div className="vaucher">
                        <CustomData
                            titleText="ticket.voucher"
                            titleTextType="I18N"
                            valueText={idTransaction.substring(0, 8)}
                        />
                    </div>
                </div>
            )}
            {renderVoucher && (
                <>
                    <div className="pending-signature">
                        <div className="horizontal-divider" />
                        <div className="pending-approval">
                            <div className="tiny-small secondary">{i18n.get("transfer.dateHourtime")}</div>
                            <div className="tiny-small font-bold secondary">
                                <FormattedDate
                                    className="small"
                                    date={creationDateTime}
                                    dateFormat="DD/MM/YYYY HH:mm:ss"
                                />
                            </div>
                        </div>
                        <div className="vaucher">
                            <CustomData
                                titleText="ticket.voucher"
                                titleTextType="I18N"
                                valueText={idTransaction.substring(0, 8)?.toUpperCase()}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

TransactionSign.propTypes = {
    creationLastName: string,
    creationFirstName: string,
    signatures: instanceOf(Array),
    showSignatures: bool,
    showSignaturesInPending: bool,
    status: string,
    noDate: bool,
    creationTime: bool,
    isSmall: bool,
    hasSignatures: bool,
    customText: string,
    hasVaucher: bool,
    hasVoucherInPending: bool,
    noHorizontalDivider: bool,
    showMoreData: bool,
    isDesktop: bool,
};

TransactionSign.defaultProps = {
    creationLastName: "",
    creationFirstName: "",
    signatures: [],
    showSignatures: true,
    showSignaturesInPending: false,
    status: "",
    noDate: false,
    creationTime: false,
    isSmall: false,
    hasSignatures: false,
    customText: "",
    hasVaucher: false,
    hasVoucherInPending: false,
    noHorizontalDivider: false,
    showMoreData: false,
    isDesktop: false,
};

export default TransactionSign;
